import Template from "../app/Template";
import { addAlert } from "../../slices/alert";
import { useAppDispatch } from "../../hooks";
import { reopenCookieSettings } from "../../utils/cookieConsent";

export default function CookiePolicy() {

  const dispatch = useAppDispatch();

  function openCookieSettings(e: React.MouseEvent) {
    e.preventDefault();
    if (!reopenCookieSettings()) {
      dispatch(addAlert("The cookie banner could not be opened. It may be blocked by an ad blocker or browser extension.", "orange"));
    }
  }

  return (
    <Template>
      <div className="px-10 lg:px-20 pt-32 text-black text-sm space-y-4">
        <h1 className="font-bold text-2xl mb-2">Cookie Policy</h1>
        <p className="text-sm text-gray-500">Last updated: August 04, 2026</p>
        <p>RaiseDiversity Limited, 71-75 Shelton Street, London, WC2H 9JQ.</p>
        <h2 className="mt-6 mb-2">Company Number: 15964024</h2>

        <p>This Cookie Policy explains what cookies are, which ones RaiseDiversity ("the Company", "We", "Us" or "Our") uses on this Website, why We use them, and how You can control them. It should be read alongside our <a href="/privacy-policy" className="underline font-bold">Privacy Policy</a>, which explains how We handle personal data more generally.</p>

        <h2 className="font-bold text-lg mt-6 mb-2">What cookies are</h2>
        <p>Cookies are small text files placed on your device when you visit a website. They are widely used to make websites work, to remember your choices, and to provide information to the site's owners. This policy also covers similar technologies such as pixels, tags and browser storage, which we refer to collectively as "cookies".</p>

        <h2 className="font-bold text-lg mt-6 mb-2">The categories of cookie we use</h2>

        <p className="font-bold mt-4">Strictly necessary</p>
        <p>Required for the Website to function. They keep you signed in, protect forms against cross-site request forgery, and remember the cookie choices you have made. These cookies cannot be switched off, and we set them without asking for consent because the Website cannot be provided without them.</p>

        <p className="font-bold mt-4">Functional and preference</p>
        <p>Remember settings and choices you make - for example the content you have favourited or filters you have applied - so the Website behaves consistently between visits.</p>

        <p className="font-bold mt-4">Analytics and performance</p>
        <p>Help us understand how the Website is used: which pages are visited, how long visitors stay, and where errors or slow pages occur. We use Google Analytics, loaded through Google Tag Manager, for this. These cookies are only set once you have given consent.</p>

        <p className="font-bold mt-4">Marketing</p>
        <p>Used to measure the performance of our content and campaigns, and to show relevant information about RaiseDiversity elsewhere. These cookies are only set once you have given consent.</p>

        <h2 className="font-bold text-lg mt-6 mb-2">Consent</h2>
        <p>When you first visit the Website, no analytics or marketing cookies are set. All non-essential storage is denied by default until you make a choice in the consent banner, and any tags that depend on it stay dormant until then.</p>
        <p>You can change or withdraw your consent at any time - withdrawing is as easy as giving it. Use the link below, or the "Cookie settings" link in the footer of every page:</p>
        <p>
          <a href="#!" onClick={openCookieSettings} className="underline font-bold">Open cookie settings</a>
        </p>
        <p>Withdrawing consent does not affect the lawfulness of any processing carried out before you withdrew it.</p>

        <h2 className="font-bold text-lg mt-6 mb-2">Third parties</h2>
        <p>Some cookies are set by third parties acting on our behalf, including Google (Google Tag Manager and Google Analytics) and CookieYes, which provides our consent banner and records your consent choice. Those providers process data under their own privacy notices.</p>

        <h2 className="font-bold text-lg mt-6 mb-2">Cookies in use on this site</h2>
        <p>The table below is generated from an automated scan of this Website and lists the individual cookies in each category, together with their purpose and how long they last.</p>
        {/* Populated by the CookieYes script from public/index.html when the
            cookie-declaration feature is enabled for this domain. It renders
            nothing if that is off, which is why the categories above are
            written out in full rather than left to the table. */}
        <div className="cky-audit-table"></div>

        <h2 className="font-bold text-lg mt-6 mb-2">Controlling cookies in your browser</h2>
        <p>Besides our banner, every major browser lets you block or delete cookies through its own settings. Blocking strictly necessary cookies will stop parts of the Website - including signing in - from working properly.</p>

        <h2 className="font-bold text-lg mt-6 mb-2">Changes to this policy</h2>
        <p>We may update this Cookie Policy as the Website or the cookies we use change. The date at the top of this page shows when it was last revised.</p>

        <h2 className="font-bold text-lg mt-6 mb-2">Contact</h2>
        <p>If you have questions about this Cookie Policy, please get in touch through our <a href="/contact" className="underline font-bold">contact page</a>.</p>
      </div>
    </Template>
  );
}
